import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Pencil, Save, X, ChevronDown, ChevronRight, LayoutDashboard } from "lucide-react";
import { Responsive, useContainerWidth } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import {
  WIDGET_REGISTRY, WIDGET_COMPONENTS, DEFAULT_ENABLED, buildDefaultLayout,
} from "@/components/DashboardWidgets";

export default function DashboardPage() {
  const { width, containerRef, mounted } = useContainerWidth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickButtons, setQuickButtons] = useState([]);
  const [enabledWidgets, setEnabledWidgets] = useState([]);
  const [layouts, setLayouts] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const navigate = useNavigate();

  // Load data
  useEffect(() => {
    const loadAll = async () => {
      try {
        const [dashRes, btnRes, layoutRes] = await Promise.all([
          api.get("/dashboard"),
          api.get("/dashboard-buttons"),
          api.get("/user-dashboard-layout"),
        ]);
        setData(dashRes.data);
        setQuickButtons((btnRes.data.buttons || []).filter((b) => b.enabled));

        const saved = layoutRes.data;
        if (saved.enabled_widgets?.length > 0 && saved.layout?.length > 0) {
          setEnabledWidgets(saved.enabled_widgets);
          setLayouts({ lg: saved.layout });
        } else {
          setEnabledWidgets([...DEFAULT_ENABLED]);
          setLayouts({ lg: buildDefaultLayout(DEFAULT_ENABLED) });
        }
      } catch {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
        setLayoutLoaded(true);
      }
    };
    loadAll();
  }, []);

  const handleLayoutChange = useCallback((layout) => {
    if (!editMode) return;
    setLayouts({ lg: layout });
  }, [editMode]);

  const toggleWidget = useCallback((widgetId) => {
    setEnabledWidgets((prev) => {
      const isEnabled = prev.includes(widgetId);
      let next;
      if (isEnabled) {
        next = prev.filter((w) => w !== widgetId);
        setLayouts((l) => ({ lg: (l.lg || []).filter((item) => item.i !== widgetId) }));
      } else {
        next = [...prev, widgetId];
        const reg = WIDGET_REGISTRY[widgetId];
        const currentLayout = layouts.lg || [];
        const maxY = currentLayout.reduce((m, item) => Math.max(m, item.y + item.h), 0);
        setLayouts((l) => ({
          lg: [...(l.lg || []), { i: widgetId, x: 0, y: maxY, w: reg.defaultW, h: reg.defaultH }],
        }));
      }
      return next;
    });
  }, [layouts]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/user-dashboard-layout", {
        enabled_widgets: enabledWidgets,
        layout: layouts.lg || [],
      });
      toast.success("Dashboard layout saved");
      setEditMode(false);
      setSettingsOpen(false);
    } catch {
      toast.error("Failed to save layout");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSettingsOpen(false);
    // Reload saved layout
    api.get("/user-dashboard-layout").then((r) => {
      const saved = r.data;
      if (saved.enabled_widgets?.length > 0 && saved.layout?.length > 0) {
        setEnabledWidgets(saved.enabled_widgets);
        setLayouts({ lg: saved.layout });
      } else {
        setEnabledWidgets([...DEFAULT_ENABLED]);
        setLayouts({ lg: buildDefaultLayout(DEFAULT_ENABLED) });
      }
    });
  };

  // Group widgets by category for settings panel
  const categories = useMemo(() => {
    const cats = {};
    Object.entries(WIDGET_REGISTRY).forEach(([id, w]) => {
      if (!cats[w.category]) cats[w.category] = [];
      cats[w.category].push({ id, ...w });
    });
    return cats;
  }, []);

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[...Array(5)].map((_, i) => <div key={i} className="h-28 bg-white border border-gray-200 rounded-sm animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const currentLayout = layouts.lg || [];
  const filteredLayout = currentLayout.filter((item) => enabledWidgets.includes(item.i));

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Overview of your supply house operations</p>
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
                <SheetTrigger asChild>
                  <Button data-testid="widget-settings-btn" variant="outline" className="rounded-sm border-gray-300">
                    <LayoutDashboard className="w-4 h-4 mr-1.5" /> Widgets
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[340px] sm:w-[380px] overflow-y-auto" side="right">
                  <SheetHeader>
                    <SheetTitle className="font-heading text-lg">Dashboard Widgets</SheetTitle>
                    <p className="text-xs text-gray-500">Select which widgets to show on your dashboard</p>
                  </SheetHeader>
                  <div className="mt-4 space-y-2">
                    {Object.entries(categories).map(([cat, widgets]) => (
                      <CategorySection key={cat} category={cat} widgets={widgets} enabledWidgets={enabledWidgets} toggleWidget={toggleWidget} />
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
              <Button data-testid="cancel-edit-btn" variant="outline" onClick={handleCancel} className="rounded-sm border-gray-300">
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              <Button data-testid="save-layout-btn" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Layout"}
              </Button>
            </>
          ) : (
            <Button data-testid="edit-dashboard-btn" variant="outline" onClick={() => setEditMode(true)} className="rounded-sm border-gray-300 text-gray-600 hover:text-gray-800">
              <Pencil className="w-4 h-4 mr-1.5" /> Customize
            </Button>
          )}
        </div>
      </div>

      {editMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 flex items-center gap-2">
          <Pencil className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800">
            <strong>Edit Mode:</strong> Drag widgets to reorder. Click <strong>Widgets</strong> to add/remove sections. Click <strong>Save Layout</strong> when done.
          </p>
        </div>
      )}

      {/* Grid Layout */}
      {layoutLoaded && mounted && (
        <div ref={containerRef}>
          <Responsive
            width={width}
            layouts={{ lg: filteredLayout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 12, sm: 6, xs: 6, xxs: 6 }}
            rowHeight={30}
            margin={[12, 12]}
            isDraggable={editMode}
            isResizable={false}
            onLayoutChange={handleLayoutChange}
            draggableHandle=".widget-drag-handle"
            compactType="vertical"
            useCSSTransforms={true}
          >
          {filteredLayout.map((item) => {
            const Comp = WIDGET_COMPONENTS[item.i];
            if (!Comp) return null;
            const reg = WIDGET_REGISTRY[item.i];
            return (
              <div key={item.i} data-testid={`widget-${item.i}`}>
                <Card className={`h-full border ${editMode ? "border-dashed border-gray-300 shadow-md" : "border-gray-200 shadow-sm"} rounded-sm overflow-hidden transition-all`}>
                  {editMode && (
                    <div className="widget-drag-handle cursor-grab active:cursor-grabbing bg-gray-50 border-b border-gray-200 px-3 py-1.5 flex items-center justify-between">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{reg?.label}</span>
                      <span className="text-gray-300 text-xs">&#x2630;</span>
                    </div>
                  )}
                  <CardContent className={`${editMode ? "p-3" : "p-4"} h-full overflow-hidden`}>
                    <Comp data={data} navigate={navigate} quickButtons={quickButtons} />
                  </CardContent>
                </Card>
              </div>
            );
          })}
          </Responsive>
        </div>
      )}
    </div>
  );
}

function CategorySection({ category, widgets, enabledWidgets, toggleWidget }) {
  const [open, setOpen] = useState(true);
  const count = widgets.filter((w) => enabledWidgets.includes(w.id)).length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-2.5 rounded-sm hover:bg-gray-50 transition-colors" data-testid={`category-${category}`}>
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
          <span className="text-sm font-semibold text-[#1F2937]">{category}</span>
        </div>
        <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{count}/{widgets.length}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-6 pr-2 pb-2 space-y-1">
          {widgets.map((w) => (
            <label key={w.id} className="flex items-start gap-2.5 p-2 rounded-sm hover:bg-gray-50 cursor-pointer" data-testid={`widget-toggle-${w.id}`}>
              <Checkbox checked={enabledWidgets.includes(w.id)} onCheckedChange={() => toggleWidget(w.id)} className="mt-0.5" />
              <div>
                <p className="text-xs font-medium text-[#1F2937] leading-tight">{w.label}</p>
                <p className="text-[10px] text-gray-400 leading-tight mt-0.5">{w.description}</p>
              </div>
            </label>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
